import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Badge, Button, Link, PermissionsProvider} from '@/components';
import {useCountryList} from '@/context/CountryListContext';
import KYCImage from '@/screens/KYC/Details/KYCImage';
import {fileService, kycBucketKey} from '@/services/fileService';
import {AfriexVerificationStatus} from '@/types';
import {statusColors} from '@/types/Transaction';
import {mapCountryCodeToFlags} from '@/utils/countries';
import {formatDate} from '@/utils/date';
import {logger} from '@/utils/logger';

import type {UserDetailsProps} from './types';
import UserProfile from './UserProfile';

const UserDetails: React.FC<UserDetailsProps> = ({
  user,
  isLoadingUserDetails,
  toggleComplianceModal,
  toggleModal,
  setShowConfirmModal,
}) => {
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  const [selfieBase64, setSelfieBase64] = useState<string | undefined>(
    undefined,
  );
  const country = user?.currentCountry?.toUpperCase();
  const flag = mapCountryCodeToFlags(countryList)[country];
  const verifyText = 'Verify';
  const unverifyText = 'Unverify';
  const isVerified = user?.kyc?.status === AfriexVerificationStatus.Success;
  const isKYCBtnDisabled = !user?.kyc?.status;
  const dateOfBirth = user?.kyc?.dateOfBirth;
  const formattedDateOfBirth = dateOfBirth ? formatDate(dateOfBirth) : null;
  const deactivatedLabel = user.isDeactivated ? 'Deactivated' : 'Active';
  const status = user.isDeactivated ? 'failed' : 'successful';
  const veriffSelfie = user?.kyc?.images?.selfie;
  const sumSubSelfie = user?.kyc?.media?.images?.length
    ? user.kyc.media.images[user.kyc.media.images.length - 1]
    : undefined;

  const selectedSelfie = sumSubSelfie || veriffSelfie;
  const canSendToGhana = user?.permissions?.isLoyalGHSCustomer || false;

  // NOTE (pre-existing, unfixed): this is a network call in the render body,
  // not in an effect. It re-fires on every render until `setSelfieBase64`
  // lands on the same value and React bails out of the re-render, and it is
  // not cancelled when the component unmounts or `user` changes. Moving it
  // into a `useEffect` keyed on the selfie is the right fix but changes when
  // the request is made, so it belongs in its own PR.
  if (selectedSelfie && kycBucketKey) {
    fileService
      .s3ToBase64(selectedSelfie, kycBucketKey, user.id)
      .then((base64) => {
        setSelfieBase64(base64);
      })
      // Nothing else can handle this: a failed decode just leaves the selfie
      // unrendered, which is today's behaviour. `logger` is dev-only, so
      // production stays silent exactly as before.
      .catch((error) => {
        logger.error('Failed to load the KYC selfie:', error);
      });
  }

  return (
    <div className="rounded-xl shadow-xl border dark:border-dark-borderMid p-4 space-y-6 mb-6 mt-6">
      {/* KYC Actions */}
      <div className="border-b border-gray-200 dark:border-dark-borderMid">
        <div className="pl-1 space-y-2">
          <div className="flex justify-center sm:justify-start flex-wrap items-center gap-3">
            <PermissionsProvider permission={AfriexAdminPermission.UserUpdate}>
              <Button
                className="mt-1 hover:underline"
                disabled={isLoadingUserDetails}
                variant="ghost"
                onClick={toggleModal}
              >
                Update User Info
              </Button>
            </PermissionsProvider>

            <Link
              to={`/kyc/${user.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="ghost" className="hover:underline">
                View KYC details
              </Button>
            </Link>

            <PermissionsProvider permission={AfriexAdminPermission.KYCUpdate}>
              <Button
                disabled={isKYCBtnDisabled}
                variant="ghost"
                onClick={toggleComplianceModal}
                className="hover:underline"
              >
                Request KYC Docs
              </Button>
            </PermissionsProvider>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row justify-between flex-wrap gap-6">
        <div className="w-full xl:w-1/3">
          {/* Selfie */}
          <div className="mb-5 rounded-md border dark:border-dark-borderMid place-items-center self-center">
            <KYCImage
              src={selfieBase64}
              name={user?.name.fullName}
              alt="User selfie"
            />
          </div>

          {/* Basic KYC Info */}
          <div className="min-w-[250px] space-y-2 self-center">
            {/* --- User Info --- */}
            <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
                Name
              </p>
              <p className="flex-[0.6] text-sm font-medium text-gray-700  dark:text-gray-100 w-1/2 truncate">
                {user.name?.fullName}
              </p>
            </div>

            <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
                Date of Birth
              </p>
              <p className="flex-[0.6] text-sm font-medium text-gray-700  dark:text-gray-100 w-1/2 truncate">
                {formattedDateOfBirth || '-'}
              </p>
            </div>

            <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
                Country
              </p>
              <p className="flex-[0.6] text-sm font-medium text-gray-700  dark:text-gray-100 w-1/2 truncate">
                {`${country || ''} ${flag ?? '-'}`}
              </p>
            </div>

            <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
                User Status
              </p>
              <p className="flex-[0.6] text-sm font-medium w-1/2 truncate">
                <Badge
                  label={deactivatedLabel}
                  colorScheme={statusColors[status]}
                  className="px-4"
                />
              </p>
            </div>

            <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
              <p className="flex-[0.4] text-slate-500 self-center text-sm dark:text-slate-100 w-full">
                KYC Verified
              </p>
              <div className="flex-[0.6] justify-start flex gap-2 w-full">
                <Badge
                  label={isVerified ? 'Yes' : 'No'}
                  colorScheme={
                    statusColors[isVerified ? 'successful' : 'failed']
                  }
                  className="px-4 !rounded-3xl"
                />
                <PermissionsProvider
                  permission={AfriexAdminPermission.KYCUpdate}
                >
                  <Button
                    disabled={isKYCBtnDisabled}
                    variant="unStyled"
                    onClick={() => setShowConfirmModal(true)}
                    className="hover:underline text-cyan-700"
                  >
                    {isVerified ? unverifyText : verifyText}
                  </Button>
                </PermissionsProvider>
              </div>
            </div>

            <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
                Security Enabled
              </p>
              <p className="flex-[0.6] text-sm font-medium w-1/2 truncate text-gray-700 dark:text-gray-100">
                {user.isSecurityFlagged ? 'True' : 'False'}
              </p>
            </div>

            {/* Loyalty */}
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
                Can Send To Ghana
              </p>
              <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
                <Badge
                  label={canSendToGhana ? 'Yes' : 'No'}
                  colorScheme={
                    statusColors[canSendToGhana ? 'successful' : 'failed']
                  }
                  className="px-4"
                />
              </p>
            </div>
          </div>
        </div>
        <div className="w-full xl:flex-1">
          <UserProfile
            user={user}
            showUserID
            showEmail
            showDateOpened
            showPhone
            showUsername
            showReferredBy
            showDeviceId
            showDeviceType
            showAppVersion
            showVirtualAccount
            showStripeUser
            showRegistrationIP
            showUserLogs
          />
        </div>
      </div>
    </div>
  );
};

export default UserDetails;
